import { useEffect } from 'react';

declare const __APP_VERSION__: string;

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// sessionStorage key that records the last server version we actually reloaded
// for. Persists across location.reload() but clears on tab close, so users get
// a fresh attempt each session. This breaks the infinite-reload loop that
// occurs when the SW still serves the stale bundle after an update.
//
// Written ONLY on the branch that really reloads. Poking the worker is
// idempotent and must stay retryable: writing the key before reg.update() meant
// that if the update did not take effect that session, no later poll ever tried
// again and the tab stayed on the old bundle indefinitely.
const RELOAD_VERSION_KEY = 'vck_v';

async function checkForUpdate() {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    const { version } = (await res.json()) as { version: string };
    if (version === __APP_VERSION__) return;

    // When a service worker is active (production/staging), calling reload()
    // directly serves the old cached bundle again. Instead, trigger the SW to
    // download the new bundle. With skipWaiting + clientsClaim set in
    // vite.config.ts the new worker then activates and claims this page.
    // No one-shot guard on this branch: reg.update() is a cheap conditional
    // request, and it has to keep retrying until the update actually lands.
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        reg.update();
        return;
      }
    }
    // Dev mode: Vite's HMR handles hot updates automatically. Reloading
    // here would interrupt in-progress operations (e.g. file uploads) and
    // can temporarily clear Firebase Auth state — breaking e2e tests.
    if (import.meta.env.DEV) return;

    // No service worker at all (unsupported browser, incognito, failed
    // registration): reload directly. This is the only branch that reloads, so
    // it is the only one that burns the one-shot.
    if (sessionStorage.getItem(RELOAD_VERSION_KEY) === version) return;
    sessionStorage.setItem(RELOAD_VERSION_KEY, version);
    window.location.reload();
  } catch {
    // network error — ignore, try again next interval
  }
}

export function useVersionCheck() {
  useEffect(() => {
    checkForUpdate();

    const interval = setInterval(checkForUpdate, POLL_INTERVAL_MS);

    // Also check when the user returns to the tab/app after being away
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
